/**
 * Shared Resource Catalog Storage Utility
 *
 * Provides sessionStorage-based persistence for the resource catalog
 * shared between Service Directory and Find Information pages.
 */

import type { ResourceListItemContent } from '@/types/content';

const CATALOG_STORAGE_KEY = 'resource-catalog';

/**
 * Extended catalog item that includes source metadata
 */
export interface CatalogStorageItem extends ResourceListItemContent {
  /** Source page that added this item */
  sourceType: 'service-directory' | 'find-information' | 'events' | 'search';
  /** Golden Buckeye status (Service Directory only) */
  isGoldenBuckeye?: boolean;
  /** Discount info (Service Directory only) */
  discount?: string;
  /** Subcategory names for display (Service Directory only) */
  subcategories?: string[];
  /** Service-specific contact info */
  serviceContact?: {
    address?: string;
    phone?: string;
    website?: string;
  };
  /** Event date display string (Events only) — e.g., "Saturday, Feb 14" */
  eventDate?: string;
  /** Event time display string (Events only) — e.g., "10:00 AM - 11:30 AM" */
  eventTime?: string;
  /** Event category name for theme pill (Events only) */
  eventCategory?: string;
  /** Pill colors from theme JSON (Events only) */
  eventThemeColors?: {
    background: string;
    border: string;
  };
}

/**
 * Get all catalog items from sessionStorage
 */
export function getCatalogItems(): CatalogStorageItem[] {
  try {
    const stored = sessionStorage.getItem(CATALOG_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors, return empty array
  }
  return [];
}

/**
 * Save catalog items to sessionStorage
 */
export function saveCatalogItems(items: CatalogStorageItem[]): void {
  try {
    sessionStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Ignore errors (quota exceeded, etc.)
  }
}

/**
 * Add an item to the catalog
 * Returns true if item was added, false if it already exists
 */
export function addCatalogItem(item: CatalogStorageItem): boolean {
  const items = getCatalogItems();
  const exists = items.some((i) => i.id === item.id);

  if (exists) {
    return false;
  }

  items.push(item);
  saveCatalogItems(items);
  return true;
}

/**
 * Remove an item from the catalog by ID
 * Returns true if item was removed, false if not found
 */
export function removeCatalogItem(itemId: string): boolean {
  const items = getCatalogItems();
  const index = items.findIndex((i) => i.id === itemId);

  if (index === -1) {
    return false;
  }

  items.splice(index, 1);
  saveCatalogItems(items);
  return true;
}

/**
 * Toggle an item in the catalog (add if not present, remove if present)
 * Returns true if item is now in catalog, false if removed
 */
export function toggleCatalogItem(item: CatalogStorageItem): boolean {
  const items = getCatalogItems();
  const index = items.findIndex((i) => i.id === item.id);

  if (index === -1) {
    items.push(item);
    saveCatalogItems(items);
    return true;
  } else {
    items.splice(index, 1);
    saveCatalogItems(items);
    return false;
  }
}

/**
 * Check if an item is in the catalog
 */
export function isInCatalog(itemId: string): boolean {
  const items = getCatalogItems();
  return items.some((i) => i.id === itemId);
}

/**
 * Get catalog item IDs
 */
export function getCatalogItemIds(): string[] {
  return getCatalogItems().map((i) => i.id);
}

/**
 * Clear all catalog items
 */
export function clearCatalog(): void {
  try {
    sessionStorage.removeItem(CATALOG_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Convert catalog items to ResourceListItemContent format for ResourceCatalog component
 */
export function toResourceListItems(items: CatalogStorageItem[]): ResourceListItemContent[] {
  return items.map((item) => ({
    id: item.id,
    type: item.type,
    title: item.title,
    source: item.source,
    description: item.description,
    href: item.href,
    actions: item.actions,
  }));
}
